import { usePermissionsStore } from "../permissions/permissions_store";
import { allowedRecordCategories, searchPermissionScope } from "../../config/search-permissions";
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import feather from 'feather-icons';
import { AppContexts } from '../../providers';
import { availableFeatures } from '../../config/navigation';
import { addSearch, historyKey, readHistory, recordResult, recentVisit, searchFeatures, writeHistory } from './search-utils';
import useRecordSearch, { trackSearch } from './use-record-search';
import './search.css';

export const SearchIcon = ({ name = 'search' }) => <svg aria-hidden="true" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" dangerouslySetInnerHTML={{ __html: feather.icons[name]?.contents || '' }} />;
export const Highlight = ({ text = '', query }) => {
  const index = query ? text.toLowerCase().indexOf(query.toLowerCase()) : -1;
  return index < 0 ? text : <>{text.slice(0, index)}<mark>{text.slice(index, index + query.length)}</mark>{text.slice(index + query.length)}</>;
};
export const CATEGORY_NAMES = { bookings: 'Bookings', owners: 'Owners', contacts: 'Contacts', 'home-guides': 'Home Guides', tasks: 'Tasks' };

export const SEARCH_PAGE_CATEGORIES = ['bookings', 'owners', 'contacts', 'home-guides', 'tasks'];
const pageCategory = category => SEARCH_PAGE_CATEGORIES.includes(category) ? category : '';

function Search({ account, fullPage = false }) {
  const history = useHistory(), location = useLocation();
  const permissions = usePermissionsStore();
  const permissionScope = searchPermissionScope(permissions);
  const scope = historyKey(account);
  const allowedCategories = allowedRecordCategories(permissions, account);
  const [opened, setOpened] = useState(fullPage);
  const [query, setQuery] = useState(fullPage ? location.state?.query || '' : '');
  const [requestedCategory, setCategory] = useState(fullPage ? pageCategory(location.state?.category) : '');
  const category = allowedCategories.includes(requestedCategory) ? requestedCategory : "";
  const [page, setPage] = useState(0);
  const [saved, setSaved] = useState(() => readHistory(scope));
  const [selected, setSelected] = useState(0);
  const root = useRef(null), input = useRef(null), trigger = useRef(null);
  // The scope captures every relevant grant; ignore unrelated store state changes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const features = useMemo(() => availableFeatures(account, permissions), [account, permissionScope]);
  const recordsEnabled = opened && allowedCategories.length > 0 && account.details?.type !== "expired" && account.details?.type !== "onboarding" && !(account.guide_onboarding && !account.guide_onboarding.completed_at && !account.guide_onboarding.dismissed_at);
  const records = useRecordSearch(query, recordsEnabled, `${scope}:${permissionScope}`, category, page);
  const featureMatches = useMemo(() => searchFeatures(features, query, saved.visits), [features, query, saved.visits]);
  const listId = fullPage ? 'universal-search-page-results' : 'universal-search-results';

  const save = (data) => { setSaved(data); writeHistory(scope, data); };
  const rememberQuery = (value) => { if (value.trim()) save({ ...saved, searches: addSearch(saved.searches, value) }); };
  const close = (restoreFocus = false) => {
    if (!fullPage) { setOpened(false); setQuery(''); if (restoreFocus) trigger.current?.focus(); }
  };
  useEffect(() => {
    if (!fullPage) { setOpened(false); setQuery(''); }
    const visit = recentVisit({ pathname: location.pathname, search: location.search }, features, account);
    if (visit) {
      setSaved(previous => {
        const data = { ...previous, visits: [visit, ...previous.visits.filter(v => !v.record && v.feature !== visit.feature)].slice(0, 10) };
        writeHistory(scope, data); return data;
      });
    }
  }, [location.pathname, location.search, features, fullPage, scope, account]);
  useEffect(() => {
    if (fullPage && location.state?.query !== undefined) {
      setQuery(location.state.query); setCategory(pageCategory(location.state.category)); setPage(0);
    }
  }, [fullPage, location.state]);
  useEffect(() => {
    if (opened) input.current?.focus();
  }, [opened]);
  useEffect(() => {
    if (fullPage) return;
    const keydown = event => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault(); setOpened(true); input.current?.focus();
      }
      if (event.key === 'Escape' && opened) { setOpened(false); setQuery(''); trigger.current?.focus(); }
    };
    const outside = event => { if (!root.current?.contains(event.target)) { setOpened(false); setQuery(''); } };
    document.addEventListener('keydown', keydown); document.addEventListener('pointerdown', outside);
    return () => { document.removeEventListener('keydown', keydown); document.removeEventListener('pointerdown', outside); };
  }, [fullPage, opened]);

  const select = (result) => {
    if (result.repeat) { setQuery(result.name); setSelected(0); input.current?.focus(); return; }
    if (result.viewAll) {
      rememberQuery(query);
      if (fullPage) { setCategory(pageCategory(result.category)); setPage(0); return; }
      close();
      history.push({ pathname: '/search', state: { query, category: pageCategory(result.category) } }); return;
    }
    save({ searches: query.trim() ? addSearch(saved.searches, query) : saved.searches, visits: saved.visits });
    trackSearch({ event: 'select', category: result.category, feature: result.record ? undefined : result.id, found: true });
    close(); history.push(result.route);
  };

  const groups = [];
  if (!query.trim()) {
    const recent = saved.visits.filter(visit => !visit.record).map(visit => features.find(f => f.id === visit.feature)).filter(Boolean);
    if (recent.length) groups.push({ label: 'Recently Visited', items: recent });
    if (saved.searches.length) groups.push({ label: 'Recent Searches', items: saved.searches.map(name => ({ id: `recent:${name}`, name, icon: 'clock', repeat: true })) });
    const quickIds = ['messages', 'guests', 'contacts', 'tasks', 'homeGuides'];
    groups.push({ label: 'Quick Navigation', items: features.filter(f => quickIds.includes(f.id)).slice(0, 5) });
  } else {
    if (!category && featureMatches.length) groups.push({ label: 'Go To', items: featureMatches.slice(0, fullPage ? 100 : 5), ...(featureMatches.length > 5 && !fullPage ? { more: '' } : {}) });
    records.groups.filter(group => allowedCategories.includes(group.category) && (!fullPage || SEARCH_PAGE_CATEGORIES.includes(group.category))).forEach(group => {
      const items = group.items.map(item => {
        const result = recordResult(item, group.category, account, permissions);
        return result ? { ...result, source: item } : null;
      }).filter(Boolean);
      if (items.length || group.has_more) groups.push({ label: CATEGORY_NAMES[group.category], items, ...(group.has_more && SEARCH_PAGE_CATEGORIES.includes(group.category) && (!fullPage || !category) ? { more: group.category } : {}) });
    });
  }
  const rows = groups.flatMap(group => [...group.items, ...(group.more !== undefined ? [{ id: `all:${group.more}`, name: `View all ${group.label.toLowerCase()} results`, viewAll: true, category: group.more, icon: 'arrow-right' }] : [])]);
  useEffect(() => { setSelected(0); }, [query, category, page]);
  useEffect(() => { setSelected(index => Math.min(index, Math.max(0, rows.length - 1))); }, [rows.length]);
  useEffect(() => {
    if (!opened || !query.trim() || records.loading || records.error) return;
    const timer = setTimeout(() => trackSearch({ event: 'results', found: rows.length > 0, category: category || 'features', feature: featureMatches[0]?.id }), 500);
    return () => clearTimeout(timer);
  }, [opened, query, records.loading, records.error, rows.length, category, featureMatches]);
  useEffect(() => {
    root.current?.querySelector('[aria-selected="true"]')?.scrollIntoView?.({ block: 'nearest' });
  }, [selected]);
  const onKeyDown = event => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault(); setSelected(index => rows.length ? (index + (event.key === 'ArrowDown' ? 1 : -1) + rows.length) % rows.length : 0);
    } else if (event.key === 'Enter') {
      event.preventDefault(); if (rows[selected]) select(rows[selected]); else rememberQuery(query);
    } else if (event.key === 'Escape') { event.preventDefault(); close(true); }
  };
  let rowIndex = -1;
  return <div ref={root} className={`rbr-universal-search ${fullPage ? 'search-full-page' : ''} ${opened ? 'is-expanded' : ''}`}>
    {!fullPage && <button type="button" className="rbr-search-trigger" aria-label="Open universal search" aria-keyshortcuts="Meta+K Control+K" aria-expanded={opened} aria-controls={listId} title="Search (⌘K / Ctrl+K)" ref={trigger} onClick={() => setOpened(value => !value)}><SearchIcon /></button>}
    {opened && <div className="rbr-search-expanded">
      <div className="rbr-search-field"><SearchIcon /><input ref={input} aria-label="Universal search" role="combobox" aria-expanded="true" aria-autocomplete="list" aria-controls={listId} aria-activedescendant={rows[selected] ? `${listId}-${selected}` : undefined} autoComplete="off" maxLength={120} placeholder="Search features, bookings, home guides, tasks, and more…" value={query} onChange={event => { setQuery(event.target.value); setPage(0); }} onKeyDown={onKeyDown} /><button type="button" aria-label={fullPage ? 'Clear search' : 'Close search'} onClick={() => fullPage ? setQuery('') : close(true)}><SearchIcon name="x" /></button></div>
      <div className="rbr-search-dropdown">
        {fullPage && <div className="rbr-search-category">
          <div className="rbr-pill-toggle" role="group" aria-label="Search category">
            {['', ...SEARCH_PAGE_CATEGORIES.filter(key => allowedCategories.includes(key))].map(key => <button
              type="button"
              key={key}
              className={`rbr-pill-toggle-button${category === key ? ' is-active' : ''}`}
              aria-pressed={category === key}
              onClick={() => { setCategory(key); setPage(0); }}
            >{key ? CATEGORY_NAMES[key] : 'All results'}</button>)}
          </div>
        </div>}
        <div className="rbr-search-scroll" id={listId} role="listbox" aria-label="Search results" aria-busy={records.loading}>
          {groups.map(group => <div key={group.label} role="group" aria-label={group.label}>
            <div className="rbr-search-group-title">{group.label}{group.label === 'Recent Searches' && <button type="button" onClick={() => save({ ...saved, searches: [] })}>Clear History</button>}</div>
            {[...group.items, ...(group.more !== undefined ? [{ id: `all:${group.more}`, name: `View all ${group.label.toLowerCase()} results`, viewAll: true, category: group.more, icon: 'arrow-right' }] : [])].map(result => {
              const index = ++rowIndex;
              return <div className="rbr-search-row-wrap" key={result.id}><div role="option" id={`${listId}-${index}`} aria-selected={index === selected} className={`rbr-search-result ${index === selected ? 'is-selected' : ''}`} onMouseEnter={() => setSelected(index)} onClick={() => select(result)}>
                <SearchIcon name={result.icon} /><span className="rbr-search-label"><strong><Highlight text={result.name} query={query} /></strong>{result.description && <small><Highlight text={result.description} query={query} /></small>}</span>{!result.repeat && <SearchIcon name="arrow-up-right" />}
              </div>{result.repeat && <button type="button" className="rbr-remove-search" aria-label={`Remove search ${result.name}`} onClick={() => save({ ...saved, searches: saved.searches.filter(s => s !== result.name) })}><SearchIcon name="x" /></button>}</div>;
            })}
          </div>)}
        </div>
        <div className="rbr-search-status" role="status">{records.loading ? 'Searching records…' : records.error ? 'Records are unavailable. Feature search is still available.' : !rows.length ? (query.trim() ? 'No results found. Try a different name or feature.' : 'Start typing to search.') : query.trim().length === 1 ? 'Type at least 2 characters to search records.' : ''}</div>
        {!fullPage && <div className="rbr-search-footer"><span>↑ ↓ navigate</span><span>↵ open</span><span>esc close</span>{query.trim() && <button type="button" onClick={() => select({ viewAll: true, category: '' })}>View all results <SearchIcon name="arrow-right" /></button>}</div>}
        {fullPage && category && <div className="rbr-search-pagination"><button disabled={page === 0} onClick={() => setPage(p => p - 1)}>Previous</button><span>Page {page + 1}</span><button disabled={!records.groups.some(g => allowedCategories.includes(g.category) && g.has_more) || records.loading} onClick={() => setPage(p => p + 1)}>Next</button></div>}
      </div>
    </div>}
  </div>;
}

export default function UniversalSearch(props) {
  const { user: account } = useContext(AppContexts.AuthContext);
  return account ? <Search key={`${historyKey(account)}:${props.fullPage ? 'page' : 'header'}`} account={account} {...props} /> : null;
}
export const SearchPage = () => <main className="rbr-search-page"><h1>Search</h1><UniversalSearch fullPage /></main>;
